import { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ColDef, ColumnApi } from 'ag-grid-community';

// Approx menu width (CSS min-width 220 + padding), used only to clamp it into the viewport.
const MENU_WIDTH = 240;

interface ColumnChooserProps {
  columnApi: ColumnApi | null;
  /** The tabular columns this chooser controls; also supplies each column's display name. */
  columnDefs: ColDef[];
  /** Called after any visibility/reset change, so the caller can persist the new state. */
  onChange: () => void;
  /** Trigger content — defaults to the full "Columns" button; the in-grid header passes an icon. */
  label?: React.ReactNode;
  /** Trigger button class — override for the compact header trigger. */
  buttonClassName?: string;
}

/**
 * Column show/hide + reset chooser for the tabular grid view, driving ag-Grid's
 * ColumnApi (v22 Community has no tool-panel sidebar). Shared by the toolbar "Columns"
 * dropdown and the in-grid selection-column header. The menu is portaled to <body> so
 * it isn't clipped by an ag-grid header cell's overflow.
 *
 * Only columns present in `columnDefs` are listed, so the leading selection column and
 * any stale persisted colIds are never offered.
 */
export function ColumnChooser({
  columnApi,
  columnDefs,
  onChange,
  label = (<><i className="fas fa-sliders-h" /> <span className="caret" /></>),
  buttonClassName = 'btn btn-default dropdown-toggle',
}: ColumnChooserProps) {
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const open = menuPos != null;
  // ColumnApi state isn't reactive — force a re-read of getColumnState() after mutations.
  const [, bump] = useReducer((c: number) => c + 1, 0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const idPrefix = useId();

  const close = () => setMenuPos(null);
  const toggle = () => {
    if (open) return close();
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    setMenuPos({ top: r.bottom, left: Math.min(r.left, window.innerWidth - MENU_WIDTH - 8) });
  };

  useEffect(() => {
    if (!open) return;
    // Trigger (.column-chooser) and portaled menu (.column-chooser-menu) both count as
    // "inside" — a class check on the event target is robust for portaled content, where
    // node.contains can miss.
    const inside = (t: EventTarget | null) =>
      !!(t as Element | null)?.closest?.('.column-chooser, .column-chooser-menu');
    const onOutside = (e: MouseEvent) => { if (!inside(e.target)) close(); };
    const onScrollOrResize = (e: Event) => {
      // Scrolling within the menu's own overflow list (mouse wheel over the column list)
      // must not close it; only outside scroll/resize would drift the fixed menu.
      if (e.type === 'scroll' && inside(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const headerByColId = useMemo(() => {
    const map = new Map<string, string>();
    columnDefs.forEach((c) => {
      const colId = (c.colId ?? c.field) as string;
      map.set(colId, c.headerName ?? colId);
    });
    return map;
  }, [columnDefs]);

  if (!columnApi) return null;

  const setVisible = (colId: string, visible: boolean) => {
    columnApi.setColumnVisible(colId, visible);
    onChange();
    bump();
  };

  const reset = () => {
    columnApi.resetColumnState();
    onChange();
    bump();
  };

  // Only offer columns we have a definition (and name) for — excludes the selection column.
  const items = columnApi.getColumnState().filter((cs) => headerByColId.has(cs.colId));

  return (
    <div className="btn-group column-chooser">
      <button ref={buttonRef} type="button" className={buttonClassName} title="Columns" onClick={toggle}>
        {label}
      </button>
      {open && menuPos && createPortal(
        <ul
          ref={menuRef}
          className="dropdown-menu column-chooser-menu"
          style={{ display: 'block', position: 'fixed', top: menuPos.top, left: menuPos.left }}
        >
          {items.map(({ colId, hide }) => {
            const checkboxId = `${idPrefix}-${colId}`;
            return (
              <li key={colId}>
                <div className="checkbox checkbox-primary">
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={!hide}
                    onChange={(e) => setVisible(colId, e.target.checked)}
                  />
                  <label htmlFor={checkboxId}>{headerByColId.get(colId) ?? colId}</label>
                </div>
              </li>
            );
          })}
          <li role="separator" className="divider" />
          <li>
            <a href="#" onMouseDown={(e) => { e.preventDefault(); reset(); }}>
              <i className="fas fa-undo fa-fw" /> Reset to Default
            </a>
          </li>
        </ul>,
        document.body
      )}
    </div>
  );
}
